import React, { memo, useCallback, useMemo } from "react";
import dayjs from "dayjs";
import moment from "moment";
import {
  embryoDevCultureMediaOptions,
  embryoDevPgtOptions,
  freezingMediaOptions,
  icsiAddOnOptions,
  incubatorOptions,
  vitrificationDevicesOptions,
} from "utils/FieldValues";
import OocytesUsedTable from "./OocytesUsedTable";
import { DatePicker, Input, Select, TimePicker, Tooltip } from "antd";
import { useSelector } from "react-redux";
import { disabledFields } from "./Constant";
import EmbryologyOocyteTrackingTableHandler from "./EmbryologyOocyteTrackingTableHandler";

const EmbryologyEmbryoDevelopmentTable = (props) => {
  const {
    values,
    errors,
    touched,
    setValues,
    handleBlur,
    handleChange,
    setFieldValue,
    setFieldError,
  } = props;

  const { embryologyDetails } = useSelector(
    ({ embryologyData }) => embryologyData
  );
  const { selectedPatient } = useSelector(({ common }) => common);

  const renderTable = useMemo(() => {
    return (
      <table className="custom-table">
        <tbody>
          <TableBodyContainer
            values={values}
            setValues={setValues}
            setFieldValue={setFieldValue}
            embryologyDetails={embryologyDetails}
          />
        </tbody>
      </table>
    );
  }, [values, setValues, setFieldValue, embryologyDetails]);

  return (
    <div className="custom_table_Wrap custom_scroll_wrap pb-4">
      <div className="table-responsive">
        {renderTable}

        {selectedPatient?.type_of_patient === 4 && <OocytesUsedTable />}

        <EmbryologyOocyteTrackingTableHandler
          values={values}
          errors={errors}
          touched={touched}
          setValues={setValues}
          handleBlur={handleBlur}
          handleChange={handleChange}
          setFieldValue={setFieldValue}
          setFieldError={setFieldError}
          oocytesFieldTableData={values?.oocytes}
        />
      </div>
    </div>
  );
};

const TableBodyContainer = ({
  values,
  setValues,
  setFieldValue,
  embryologyDetails,
}) => {
  const disabledField = useCallback(
    (key) => {
      const cycleType = embryologyDetails?.cycle_type || "";

      const shouldDisableByMap = disabledFields[key]?.some((type) =>
        type === "FET+Surrogacy"
          ? cycleType.includes("FET") && cycleType.includes("Surrogacy")
          : cycleType.includes(type)
      );

      if (shouldDisableByMap) return true;

      return false;
    },
    [embryologyDetails?.cycle_type]
  );

  const changeDateFormat = useCallback((date, increaseDay) => {
    return dayjs(
      moment(date).add(increaseDay, "days").format("DD/MM/YYYY"),
      "DD/MM/YYYY"
    );
  }, []);

  const handleChangeFirstDayDate = useCallback(
    (e) => {
      const day0Date = e ? moment(new Date(e)).format("YYYY-MM-DD") : null;

      if (embryologyDetails?.cycle_type?.includes("OD")) {
        setValues({
          ...values,
          day_0_date: day0Date,
          day_1_date: e ? changeDateFormat(new Date(e), 1) : null,
          day_3_date: e ? changeDateFormat(new Date(e), 3) : null,
          day_5_date: e ? changeDateFormat(new Date(e), 5) : null,
          day_6_date: e ? changeDateFormat(new Date(e), 6) : null,
          day_7_date: e ? changeDateFormat(new Date(e), 7) : null,
        });
      } else {
        setFieldValue("day_0_date", day0Date);
      }
    },
    [setFieldValue, setValues, values, embryologyDetails, changeDateFormat]
  );

  const handleChangePickupDate = useCallback(
    (e) => {
      if (embryologyDetails?.cycle_type?.includes("OD")) {
        setFieldValue(
          "opu_date",
          e ? moment(new Date(e)).format("YYYY-MM-DD") : null
        );
      } else {
        setValues({
          ...values,
          day_0_date: e
            ? dayjs(moment(new Date(e)).format("DD/MM/YYYY"), "DD/MM/YYYY")
            : null,
          day_1_date: e ? changeDateFormat(new Date(e), 1) : null,
          day_3_date: e ? changeDateFormat(new Date(e), 3) : null,
          day_5_date: e ? changeDateFormat(new Date(e), 5) : null,
          day_6_date: e ? changeDateFormat(new Date(e), 6) : null,
          day_7_date: e ? changeDateFormat(new Date(e), 7) : null,
        });
      }
    },
    [setFieldValue, setValues, values, embryologyDetails, changeDateFormat]
  );

  const handleUpdateEmbryoDevelopment = useCallback(
    (name, value) => {
      setFieldValue(name, value);
    },
    [setFieldValue]
  );

  const tableNameTd = useMemo(() => {
    return (
      <td colSpan="7">
        <h4>EMBRYO DEVELOPMENT</h4>
      </td>
    );
  }, []);

  const LMPItem = useMemo(() => {
    return (
      <div className="custom_select_date">
        {/* // <Form.Item
      //   label="LMP"
      // // name="lmp"
      // > */}
        <label htmlFor="">LMP</label>
        <DatePicker
          placeholder="Select Date"
          name="lmp"
          format={{
            format: "DD-MM-YYYY",
            type: "mask",
          }}
          value={values?.lmp}
          // disabled={disabledField("lmp")}
          disabled
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [values]);

  const OPUDateItem = useMemo(() => {
    return (
      <div className="custom_select_date">
        {/* <Form.Item
        label="OPU Date"
      // name="opu_date"
      > */}
        <label htmlFor="">OPU Date</label>
        <DatePicker
          placeholder="9/11/1997"
          name="opu_date"
          value={
            values?.opu_date ? dayjs(values?.opu_date, "DD/MM/YYYY") : null
          }
          format={{
            format: "DD-MM-YYYY",
            type: "mask",
          }}
          onChange={handleChangePickupDate}
          // disabled={disabledField("opu_date")}
          disabled
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [handleChangePickupDate, values?.opu_date]);

  const OPUTimeItem = useMemo(() => {
    return (
      <div className="custom_select_date">
        {/* <Form.Item
          label="OPU Time"
        // name="opu_time"
        > */}
        <label htmlFor="">OPU Time</label>
        <TimePicker
          name="opu_time"
          format="h:mm a"
          value={values?.opu_time ? dayjs(values?.opu_time) : ""}
          onChange={(value) => {
            const updatedValue = value ? dayjs(value, "HH:mm:ss") : null;
            handleUpdateEmbryoDevelopment("opu_time", updatedValue);
          }}
          // disabled={disabledField("opu_time")}
          disabled
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [values?.opu_time, handleUpdateEmbryoDevelopment]);

  const ActualOPUTimeItem = useMemo(() => {
    return (
      <div className="custom_select_date">
        {/*  <Form.Item
        label="Actual OPU Time"
      // name="actual_opu_time"
      > */}
        <label htmlFor="">Actual OPU Time</label>
        <TimePicker
          name="actual_opu_time"
          format="h:mm a"
          value={values?.actual_opu_time}
          onChange={(value) => {
            const updatedValue = value ? dayjs(value, "HH:mm:ss") : null;
            handleUpdateEmbryoDevelopment("actual_opu_time", updatedValue);
          }}
          disabled={disabledField("actual_opu_time")}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [values?.actual_opu_time, disabledField, handleUpdateEmbryoDevelopment]);

  const DenudationTimeItem = useMemo(() => {
    return (
      <div className="custom_select_date">
        {/* <Form.Item
        label="Denudation time"
      // name="denudation_time"
      > */}
        <label htmlFor="">Denudation time</label>
        <TimePicker
          format="h:mm a"
          name="denudation_time"
          value={values?.denudation_time}
          onChange={(value) => {
            const updatedValue = value ? dayjs(value, "HH:mm:ss") : null;
            handleUpdateEmbryoDevelopment("denudation_time", updatedValue);
          }}
          disabled={disabledField("denudation_time")}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [values?.denudation_time, disabledField, handleUpdateEmbryoDevelopment]);

  const ICSIIvfTimeItem = useMemo(() => {
    return (
      <div className="custom_select_date">
        {/* <Form.Item
        label="ICSI/IVF TIME"
      // name="icsi_ivf_time"
      > */}
        <label htmlFor="">ICSI/IVF TIME</label>
        <TimePicker
          format="h:mm a"
          name="icsi_ivf_time"
          value={values?.icsi_ivf_time}
          onChange={(value) => {
            const updatedValue = value ? dayjs(value, "HH:mm:ss") : null;
            handleUpdateEmbryoDevelopment("icsi_ivf_time", updatedValue);
          }}
          disabled={
            disabledField("icsi_ivf_time") &&
            !embryologyDetails?.cycle_type?.includes("Oocytes Vitrification")
          }
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [
    embryologyDetails?.cycle_type,
    values?.icsi_ivf_time,
    disabledField,
    handleUpdateEmbryoDevelopment,
  ]);

  const LHItem = useMemo(() => {
    return (
      <div className="custom_normal_input">
        {/* <Form.Item
        label="LH"
      // name="lh"
      > */}
        <label htmlFor="">LH</label>
        <Input
          placeholder="Enter LH"
          name="lh"
          value={values?.lh}
          onChange={(e) => {
            const updatedValue = e.target.value;
            handleUpdateEmbryoDevelopment("lh", updatedValue);
          }}
          disabled={disabledField("lh")}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [values?.lh, disabledField, handleUpdateEmbryoDevelopment]);

  const E2Item = useMemo(() => {
    return (
      <div className="custom_normal_input">
        {/* <Form.Item
        label="E2"
      // name="e2"
      > */}
        <label htmlFor="">E2</label>
        <Input
          placeholder="Enter E2"
          name="e2"
          value={values?.e2}
          onChange={(e) => {
            const updatedValue = e.target.value;
            handleUpdateEmbryoDevelopment("e2", updatedValue);
          }}
          disabled={disabledField("e2")}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [values?.e2, disabledField, handleUpdateEmbryoDevelopment]);

  const firstTd = useMemo(() => {
    return (
      <td colSpan="9">
        <div className="embryo_development_wrap">
          <ul className="grid_wrapper">
            <li className="w_180">{LMPItem} </li>
            <li className="w_180">{OPUDateItem}</li>
            <li className="w_170">{OPUTimeItem}</li>
            <li className="w_170">{ActualOPUTimeItem}</li>
            <li className="w_170">{DenudationTimeItem}</li>
            <li className="w_170">{ICSIIvfTimeItem}</li>
            <li className="w_180">{LHItem}</li>
            <li className="w_180">{E2Item}</li>
          </ul>
        </div>
      </td>
    );
  }, [
    E2Item,
    LHItem,
    LMPItem,
    OPUTimeItem,
    OPUDateItem,
    ICSIIvfTimeItem,
    ActualOPUTimeItem,
    DenudationTimeItem,
  ]);

  const EmbryoDevIncubatorItem = useMemo(() => {
    return (
      <div className="custom_select">
        {/* <Form.Item
          label="Incubator"
          // name="embryo_dev_incubator"
          className="custom_select"
        > */}
        <label htmlFor="">Incubator</label>
        <Select
          showSearch
          disabled={disabledField("embryo_dev_incubator")}
          allowClear={true}
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          placeholder="Select"
          name="embryo_dev_incubator"
          value={values?.embryo_dev_incubator}
          onChange={(value) => {
            // dispatch(
            //   setEmbryologyDetails({
            //     ...embryologyDetails,
            //     embryo_dev_incubator: value ? value : null,
            //     embryo_dev_incubator_other: null
            //   })
            // );
            // form.setFieldsValue({
            //   embryo_dev_incubator_other: null
            // });
            setValues({
              ...values,
              embryo_dev_incubator: value ? value : null,
              embryo_dev_incubator_other: null,
            });
          }}
          options={incubatorOptions}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [disabledField, setValues, values]);

  const IncubatorOtherItem = useMemo(() => {
    return (
      <div className="custom_normal_input">
        {/* // <Form.Item
      //   label="Incubator Other"
      // // name="embryo_dev_incubator_other"
      // > */}
        <label htmlFor="">Incubator Other</label>
        <Input
          placeholder="Incubator Other"
          name="embryo_dev_incubator_other"
          value={values?.embryo_dev_incubator_other}
          onChange={(e) => {
            const value = e.target.value || null;
            handleUpdateEmbryoDevelopment("embryo_dev_incubator_other", value);
          }}
        />
        {/* // </Form.Item> */}
      </div>
    );
  }, [handleUpdateEmbryoDevelopment, values?.embryo_dev_incubator_other]);

  const CultureMediaItem = useMemo(() => {
    return (
      <div className="custom_select">
        {/* <Form.Item
          label="Culture Media"
          // name="embryo_dev_culture_media"
          className="custom_select"
        > */}
        <label htmlFor="">Culture Media</label>
        <Select
          showSearch
          disabled={disabledField("embryo_dev_culture_media")}
          allowClear={true}
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          placeholder="Select"
          name="embryo_dev_culture_media"
          value={values?.embryo_dev_culture_media}
          onChange={(value) => {
            // dispatch(
            //   setEmbryologyDetails({
            //     ...embryologyDetails,
            //     embryo_dev_culture_media: value ? value : null,
            //     embryo_dev_culture_media_other: null
            //   })
            // );
            // form.setFieldsValue({
            //   embryo_dev_culture_media_other: null
            // });
            setValues({
              ...values,
              embryo_dev_culture_media: value ? value : null,
              embryo_dev_culture_media_other: null,
            });
          }}
          options={embryoDevCultureMediaOptions}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [disabledField, setValues, values]);

  const CultureMediaOtherItem = useMemo(() => {
    return (
      <div className="custom_normal_input">
        {/* <Form.Item
        label="Culture Media Other"
      // name="embryo_dev_culture_media_other"
      > */}
        <label htmlFor="">Culture Media Other</label>
        <Input
          placeholder="Culture Media Other"
          name="embryo_dev_culture_media_other"
          value={values?.embryo_dev_culture_media_other}
          onChange={(e) => {
            const value = e.target.value || null;
            handleUpdateEmbryoDevelopment(
              "embryo_dev_culture_media_other",
              value
            );
          }}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [handleUpdateEmbryoDevelopment, values?.embryo_dev_culture_media_other]);

  const BatchNoAndExpDate = useMemo(() => {
    return (
      <div className="custom_normal_input">
        {/* <Form.Item
        label="Batch No. & Expiry Date"
      // name="embryo_dev_batch_no_exp_date"
      > */}
        <label htmlFor="">Batch No. & Expiry Date</label>
        <Input
          placeholder="Batch No. & Expiry Date"
          name="embryo_dev_batch_no_exp_date"
          value={values?.embryo_dev_batch_no_exp_date}
          onChange={(e) => {
            const value = e.target.value || null;

            handleUpdateEmbryoDevelopment(
              "embryo_dev_batch_no_exp_date",
              value
            );
          }}
          disabled={disabledField("embryo_dev_batch_no_exp_date")}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [
    values?.embryo_dev_batch_no_exp_date,
    disabledField,
    handleUpdateEmbryoDevelopment,
  ]);

  const PGTItem = useMemo(() => {
    return (
      <div className="custom_select">
        {/* <Form.Item
          label="PGT ?"
          // name="embryo_dev_pgt"
        > */}
        <label htmlFor="">PGT ?</label>
        <Select
          showSearch
          disabled={disabledField("embryo_dev_pgt")}
          allowClear={true}
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          placeholder="Select"
          name="embryo_dev_pgt"
          value={values?.embryo_dev_pgt}
          onChange={(value) => {
            const fieldValue = value ? value : null;
            handleUpdateEmbryoDevelopment("embryo_dev_pgt", fieldValue);
          }}
          options={embryoDevPgtOptions}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [values?.embryo_dev_pgt, disabledField, handleUpdateEmbryoDevelopment]);

  const AddOnItem = useMemo(() => {
    return (
      <div className="custom_select">
        {/* <Form.Item
          label="ICSI-ADD ON"
          // name="add_on"
          className="custom_select"
        > */}
        <label htmlFor="">ICSI-ADD ON</label>
        <Select
          showSearch
          disabled={disabledField("add_on")}
          allowClear={true}
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          // filterSort={(optionA, optionB) =>
          //   optionA.label
          //     .toLowerCase()
          //     .localeCompare(optionB.label.toLowerCase())
          // }
          placeholder="Select"
          name="add_on"
          mode="multiple"
          maxTagCount="responsive"
          // value={embryologyDetails?.add_on}
          value={values?.add_on}
          onChange={(value) => {
            // dispatch(
            //   setEmbryologyDetails({
            //     ...embryologyDetails,
            //     add_on: value || null,
            //     add_on_other:
            //       value === "Other" ? embryologyDetails.add_on_other : null
            //   })
            // );
            // form.setFieldsValue({
            //   add_on_other: value ? embryologyDetails.add_on_other : null
            // });

            setValues({
              ...values,
              add_on: value || null,
              add_on_other: value === "Other" ? values.add_on_other : null,
            });
          }}
          options={icsiAddOnOptions}
          maxTagPlaceholder={(omittedValues) => (
            <Tooltip title={omittedValues.map(({ label }) => label).join(", ")}>
              <span>Hover Me</span>
            </Tooltip>
          )}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [disabledField, setValues, values]);

  const ProgesteroneItem = useMemo(() => {
    return (
      <div className="custom_normal_input">
        {/* <Form.Item
        label="Prog"
      // name="progesterone"
      > */}
        <label htmlFor="">Prog</label>
        <Input
          disabled={disabledField("progesterone")}
          placeholder="Enter Progesterone"
          name="progesterone"
          // value={embryologyDetails?.progesterone}
          value={values?.progesterone}
          onChange={(e) => {
            const fieldValue = e.target.value || null;
            handleUpdateEmbryoDevelopment("progesterone", fieldValue);
          }}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [values?.progesterone, disabledField, handleUpdateEmbryoDevelopment]);

  const AMHItem = useMemo(() => {
    return (
      <div className="custom_normal_input">
        {/* <Form.Item
        label="AMH"
      // name="amh"
      > */}
        <label htmlFor="">AMH</label>
        <Input
          placeholder="Enter AMH"
          name="amh"
          // value={embryologyDetails?.amh}
          value={values?.amh}
          onChange={(e) => {
            const fieldValue = e.target.value || null;
            handleUpdateEmbryoDevelopment("amh", fieldValue);
          }}
          disabled={disabledField("amh")}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [values?.amh, disabledField, handleUpdateEmbryoDevelopment]);

  const secondTd = useMemo(() => {
    return (
      <td colSpan="9">
        <div className="embryo_development_wrap">
          <ul className="grid_wrapper">
            <li className="w_250">{EmbryoDevIncubatorItem}</li>
            {values?.embryo_dev_incubator === "Other" && (
              <li className="w_220 w_xs_100">{IncubatorOtherItem}</li>
            )}
            <li className="w_250">{CultureMediaItem}</li>
            {values?.embryo_dev_culture_media === "Other" && (
              <li className="w_220 w_xs_100">{CultureMediaOtherItem}</li>
            )}
            <li className="w_250">{BatchNoAndExpDate}</li>
            <li className="w_250">{PGTItem}</li>
            <li className="w_300">{AddOnItem}</li>

            <li className="w_170">{ProgesteroneItem}</li>
            <li className="w_180"> {AMHItem}</li>
          </ul>
        </div>
      </td>
    );
  }, [
    AMHItem,
    AddOnItem,
    CultureMediaItem,
    BatchNoAndExpDate,
    IncubatorOtherItem,
    CultureMediaOtherItem,
    EmbryoDevIncubatorItem,
    PGTItem,
    ProgesteroneItem,
    values?.embryo_dev_incubator,
    values?.embryo_dev_culture_media,
  ]);

  const Day0Date = useMemo(() => {
    return (
      <div className="custom_select_date">
        {/* <Form.Item
        label="D0 Date"
      // name="day_0_date"
      > */}
        <label htmlFor="">D0 Date</label>
        <DatePicker
          placeholder="DD/MM/YYYY"
          name="day_0_date"
          disabled={!embryologyDetails?.cycle_type?.includes("OD") || !embryologyDetails?.cycle_type?.includes("ED")}
          value={
            values?.day_0_date ? dayjs(values?.day_0_date, "YYYY-MM-DD") : null
          }
          format={{
            format: "DD-MM-YYYY",
            type: "mask",
          }}
          onChange={handleChangeFirstDayDate}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [
    values?.day_0_date,
    embryologyDetails?.cycle_type,
    handleChangeFirstDayDate,
  ]);

  const Day0Time = useMemo(() => {
    return (
      <div className="custom_select_date">
        {/* <Form.Item
        label="Time"
      // name="day_0_time"
      > */}
        <label htmlFor="">Time</label>
        <TimePicker
          format="h:mm a"
          disabled={!embryologyDetails?.cycle_type?.includes("OD") || !embryologyDetails?.cycle_type?.includes("ED")}
          name="day_0_time"
          value={values?.day_0_time}
          onChange={(value) => {
            const fieldValue = value ? dayjs(value, "HH:mm:ss") : null;
            handleUpdateEmbryoDevelopment("day_0_time", fieldValue);
          }}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [
    values?.day_0_time,
    embryologyDetails?.cycle_type,
    handleUpdateEmbryoDevelopment,
  ]);

  const HRSPostICSI = useMemo(() => {
    return (
      <div className="custom_normal_input">
        {/* <Form.Item
        label="Hrs. post ICSI"
      // name="day_0_hrs_post_icsi"
      > */}
        <label htmlFor="">Hrs. post ICSI</label>
        <Input
          placeholder="Enter ICSI"
          disabled={disabledField("day_0_hrs_post_icsi")}
          name="day_0_hrs_post_icsi"
          value={values?.day_0_hrs_post_icsi}
          onChange={(e) => {
            const fieldValue = e.target.value;
            handleUpdateEmbryoDevelopment("day_0_hrs_post_icsi", fieldValue);
          }}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [
    disabledField,
    values?.day_0_hrs_post_icsi,
    handleUpdateEmbryoDevelopment,
  ]);

  const thirdTd = useMemo(() => {
    return (
      <td>
        <h4>Day 0</h4>
        <ul className="grid_wrapper">
          <li className="w_200">{Day0Date}</li>
          <li className="w_180">{Day0Time}</li>
          <li className="w_220">{HRSPostICSI}</li>
        </ul>
      </td>
    );
  }, [Day0Date, Day0Time, HRSPostICSI]);

  const Day1Date = useMemo(() => {
    return (
      <div className="custom_select_date">
        {/* <Form.Item
        label="D1 Date"
      // name="day_1_date"
      > */}
        <label htmlFor="">D1 Date</label>
        <DatePicker
          placeholder="9/11/1997"
          name="day_1_date"
          value={
            values?.day_1_date ? dayjs(values?.day_1_date, "YYYY-MM-DD") : null
          }
          format={{
            format: "DD-MM-YYYY",
            type: "mask",
          }}
          onChange={(e) => {
            const fieldValue = e
              ? moment(new Date(e)).format("YYYY-MM-DD")
              : null;
            handleUpdateEmbryoDevelopment("day_1_date", fieldValue);
          }}
          disabled
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [values?.day_1_date, handleUpdateEmbryoDevelopment]);

  const fourthTd = useMemo(() => {
    return (
      <td>
        <h4>Day 1</h4>
        <ul className="grid_wrapper">
          <li className="w_200">{Day1Date}</li>
        </ul>
      </td>
    );
  }, [Day1Date]);

  const Day3Date = useMemo(() => {
    return (
      <div className="custom_select_date">
        {/* <Form.Item
        label="D3 Date"
      // name="day_3_date"
      > */}
        <label htmlFor="">D3 Date</label>
        <DatePicker
          disabled={true}
          placeholder="9/11/1997"
          name="day_3_date"
          value={
            values?.day_3_date ? dayjs(values?.day_3_date, "YYYY-MM-DD") : null
          }
          format={{
            format: "DD-MM-YYYY",
            type: "mask",
          }}
          onChange={(e) => {
            const fieldValue = e
              ? moment(new Date(e)).format("YYYY-MM-DD")
              : null;
            handleUpdateEmbryoDevelopment("day_3_date", fieldValue);
          }}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [values?.day_3_date, handleUpdateEmbryoDevelopment]);

  const Day5Date = useMemo(() => {
    return (
      <div className="custom_select_date">
        {/* <Form.Item
        label="D5 Date"
      // name="day_5_date"
      > */}
        <label htmlFor="">D5 Date</label>
        <DatePicker
          disabled={true}
          placeholder="9/11/1997"
          name="day_5_date"
          value={
            values?.day_5_date ? dayjs(values?.day_5_date, "YYYY-MM-DD") : null
          }
          format={{
            format: "DD-MM-YYYY",
            type: "mask",
          }}
          onChange={(e) => {
            const fieldValue = e
              ? moment(new Date(e)).format("YYYY-MM-DD")
              : null;
            handleUpdateEmbryoDevelopment("day_5_date", fieldValue);
          }}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [values?.day_5_date, handleUpdateEmbryoDevelopment]);

  const Day6Date = useMemo(() => {
    return (
      <div className="custom_select_date">
        {/* <Form.Item
        label="D6 Date"
      // name="day_6_date"
      > */}
        <label htmlFor="">D6 Date</label>
        <DatePicker
          disabled={true}
          placeholder="9/11/1997"
          name="day_6_date"
          value={
            values?.day_6_date ? dayjs(values?.day_6_date, "YYYY-MM-DD") : null
          }
          format={{
            format: "DD-MM-YYYY",
            type: "mask",
          }}
          onChange={(e) => {
            const fieldValue = e
              ? moment(new Date(e)).format("YYYY-MM-DD")
              : null;
            handleUpdateEmbryoDevelopment("day_6_date", fieldValue);
          }}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [values?.day_6_date, handleUpdateEmbryoDevelopment]);

  const Day7Date = useMemo(() => {
    return (
      <div className="custom_select_date">
        {/* <Form.Item
        label="D7 Date"
      //  name="day_7_date"
      > */}
        <label htmlFor="">D7 Date</label>
        <DatePicker
          disabled={true}
          placeholder="9/11/1997"
          name="day_7_date"
          value={
            values?.day_7_date ? dayjs(values?.day_7_date, "YYYY-MM-DD") : null
          }
          format={{
            format: "DD-MM-YYYY",
            type: "mask",
          }}
          onChange={(e) => {
            const fieldValue = e
              ? moment(new Date(e)).format("YYYY-MM-DD")
              : null;
            handleUpdateEmbryoDevelopment("day_7_date", fieldValue);
          }}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [values?.day_7_date, handleUpdateEmbryoDevelopment]);

  const fifthTd = useMemo(() => {
    return (
      <td>
        <h4>Day 3/5/6/7</h4>
        <ul className="grid_wrapper">
          <li className="w_180">{Day3Date}</li>
          <li className="w_180">{Day5Date}</li>
          <li className="w_180">{Day6Date}</li>
          <li className="w_180">{Day7Date}</li>
        </ul>
      </td>
    );
  }, [Day3Date, Day5Date, Day6Date, Day7Date]);

  const FreezingMediaItem = useMemo(() => {
    return (
      <div className="custom_select">
        {/* <Form.Item
          label="Freezing Media"
          // name="freezing_media"
          className="custom_select"
        > */}
        <label htmlFor="">Freezing Media</label>
        <Select
          showSearch
          disabled={disabledField("freezing_media")}
          allowClear={true}
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          placeholder="Select"
          name="freezing_media"
          value={values?.freezing_media}
          onChange={(value) => {
            // dispatch(
            //   setEmbryologyDetails({
            //     ...embryologyDetails,
            //     freezing_media: value ? value : null,
            //     freezing_media_other: null
            //   })
            // );
            // form.setFieldsValue({
            //   freezing_media_other: null
            // });

            setValues({
              ...values,
              freezing_media_other: null,
              freezing_media: value ? value : null,
            });
          }}
          options={freezingMediaOptions}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [disabledField, setValues, values]);

  const FreezingMediaOtherItem = useMemo(() => {
    return (
      <div className="custom_normal_input">
        {/* <Form.Item
        label="Freezing Media Other"
      // name="freezing_media_other"
      > */}
        <label htmlFor="">Freezing Media Other</label>
        <Input
          placeholder="Enter Freezing Media Other"
          name="freezing_media_other"
          value={values?.freezing_media_other}
          onChange={(e) => {
            const fieldValue = e.target.value;
            handleUpdateEmbryoDevelopment("freezing_media_other", fieldValue);
          }}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [values?.freezing_media_other, handleUpdateEmbryoDevelopment]);

  const BatchNoAndExpiryDate = useMemo(() => {
    return (
      <div className="custom_normal_input">
        {/* <Form.Item
        label="Batch no. & Expiry Date"
      // name="vitrification_batch_no_exp_date"
      > */}
        <label htmlFor="">Batch no. & Expiry Date</label>
        <Input
          placeholder="Batch no. & Expiry Date"
          name="vitrification_batch_no_exp_date"
          value={values?.vitrification_batch_no_exp_date}
          onChange={(e) => {
            const fieldValue = e.target.value;
            handleUpdateEmbryoDevelopment(
              "vitrification_batch_no_exp_date",
              fieldValue
            );
          }}
          disabled={disabledField("vitrification_batch_no_exp_date")}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [
    disabledField,
    handleUpdateEmbryoDevelopment,
    values?.vitrification_batch_no_exp_date,
  ]);

  const VitrificationDevicesItem = useMemo(() => {
    return (
      <div className="custom_select">
        {/* <Form.Item
        label="Vitrification Devices"
        // name="vitrification_devices"
        className="custom_select"
      > */}
        <label htmlFor="">Vitrification Devices</label>
        <Select
          showSearch
          disabled={disabledField("vitrification_devices")}
          allowClear={true}
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          placeholder="Select"
          name="vitrification_devices"
          value={values?.vitrification_devices}
          onChange={(value) => {
            // dispatch(
            //   setEmbryologyDetails({
            //     ...embryologyDetails,
            //     vitrification_devices: value ? value : null,
            //     vitrification_devices_other: null
            //   })
            // );
            // form.setFieldsValue({
            //   vitrification_devices_other: null
            // });

            setValues({
              ...values,
              vitrification_devices_other: null,
              vitrification_devices: value ? value : null,
            });
          }}
          options={vitrificationDevicesOptions}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [disabledField, setValues, values]);

  const VitrificationDeviceOtherItem = useMemo(() => {
    return (
      <div className="custom_normal_input">
        {/* <Form.Item
        label="Vitrification Device Other"
      // name="vitrification_devices_other"
      > */}
        <label htmlFor="">Vitrification Device Other</label>
        <Input
          placeholder="Vitrification Device Other"
          name="vitrification_devices_other"
          value={values?.vitrification_devices_other}
          onChange={(e) => {
            const fieldValue = e.target.value;
            handleUpdateEmbryoDevelopment(
              "vitrification_devices_other",
              fieldValue
            );
          }}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [handleUpdateEmbryoDevelopment, values?.vitrification_devices_other]);

  const TotalNoOfGobletItem = useMemo(() => {
    return (
      <div className="custom_normal_input">
        {/* <Form.Item
        label="Total No. Of Goblet"
      // name="total_goblet"
      > */}
        <label htmlFor="">Total No. Of Goblet</label>
        <Input
          placeholder="Total No. Of Goblet"
          disabled={disabledField("total_goblet")}
          name="total_goblet"
          value={values?.total_goblet}
          onChange={(e) => {
            const fieldValue = e.target.value || null;
            handleUpdateEmbryoDevelopment("total_goblet", fieldValue);
          }}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [disabledField, handleUpdateEmbryoDevelopment, values?.total_goblet]);

  const TotalNoOfStrawItem = useMemo(() => {
    return (
      <div className="custom_normal_input">
        {/* <Form.Item
        label="Total No. Of Straw"
      // name="total_straw"
      > */}
        <label htmlFor="">Total No. Of Straw</label>
        <Input
          disabled={true}
          placeholder="Total No. Of Straw"
          // name="total_straw"
          value={values?.total_straw}
          onChange={(e) => {
            const fieldValue = e.target.value || 0;
            handleUpdateEmbryoDevelopment("total_straw", fieldValue);
          }}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [values?.total_straw, handleUpdateEmbryoDevelopment]);

  const TankNoItem = useMemo(() => {
    return (
      <div className="custom_normal_input">
        {/* <Form.Item
        label="Tank No."
      // name="tank_no"
      > */}
        <label htmlFor="">Tank No.</label>
        <Input
          placeholder="Tank No."
          disabled={disabledField("tank_no")}
          name="tank_no"
          value={values?.tank_no}
          onChange={(e) => {
            const fieldValue = e.target.value;
            handleUpdateEmbryoDevelopment("tank_no", fieldValue);
          }}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [disabledField, values?.tank_no, handleUpdateEmbryoDevelopment]);

  const CannisterNoItem = useMemo(() => {
    return (
      <div className="custom_normal_input">
        {/* <Form.Item
        label="Cannister No."
      // name="cannister_no"
      > */}
        <label htmlFor="">Cannister No.</label>
        <Input
          placeholder="Cannister No."
          name="cannister_no"
          value={values?.cannister_no}
          onChange={(e) => {
            const fieldValue = e.target.value;
            handleUpdateEmbryoDevelopment("cannister_no", fieldValue);
          }}
          disabled={disabledField("cannister_no")}
        />
        {/* </Form.Item> */}
      </div>
    );
  }, [disabledField, values?.cannister_no, handleUpdateEmbryoDevelopment]);

  const sixthTd = useMemo(() => {
    return (
      <td>
        <h4>Vitrification Details</h4>
        <ul className="grid_wrapper">
          <li className="w_150">{FreezingMediaItem}</li>
          {values?.freezing_media === "Other" && (
            <li>{FreezingMediaOtherItem}</li>
          )}
          <li className="w_180">{BatchNoAndExpiryDate}</li>
          <li className="w_170">{VitrificationDevicesItem}</li>
          {values?.vitrification_devices === "Other" && (
            <li>{VitrificationDeviceOtherItem}</li>
          )}
          <li className="w_180">{TotalNoOfGobletItem}</li>
          <li className="w_150">{TotalNoOfStrawItem}</li>
          <li className="w_150">{TankNoItem}</li>
          <li className="w_150">{CannisterNoItem}</li>
        </ul>
      </td>
    );
  }, [
    BatchNoAndExpiryDate,
    CannisterNoItem,
    FreezingMediaItem,
    FreezingMediaOtherItem,
    TankNoItem,
    TotalNoOfGobletItem,
    TotalNoOfStrawItem,
    VitrificationDeviceOtherItem,
    VitrificationDevicesItem,
    values?.freezing_media,
    values?.vitrification_devices,
  ]);

  const sevenTd = useMemo(() => {
    return (
      <>
        <td className="w_90"></td>
        <td className="w_90"></td>
        <td className="w_170"></td>
        <td className="w_120"></td>
        <td className="w_170"></td>
        <td className="w_150"></td>
        <td className="w_170"></td>
        <td className="w_170"></td>
        <td className="w_170"></td>
        <td className="w_170"></td>
        <td className="w_170"></td>
        <td className="w_170"></td>
        <td className="w_120"></td>
        <td className="w_120"></td>
        <td className="w_120"></td>
      </>
    );
  }, []);

  const renderRow = useMemo(() => {
    return (
      <>
        <tr>{tableNameTd}</tr>
        <tr>{firstTd}</tr>
        <tr>{secondTd}</tr>
        <tr className="respnsive_table_width">
          {thirdTd}
          {fourthTd}
          {fifthTd}
          {sixthTd}
        </tr>
        {embryologyDetails?.cycle_type?.includes("OD") && (
          <>
            <tr>{sevenTd}</tr>
            <tr className="respnsive_table_width">
              <td colSpan={16}></td>
            </tr>
          </>
        )}
      </>
    );
  }, [
    firstTd,
    secondTd,
    thirdTd,
    fourthTd,
    fifthTd,
    sixthTd,
    sevenTd,
    tableNameTd,
    embryologyDetails?.cycle_type,
  ]);

  return <>{renderRow}</>;
};

export default memo(EmbryologyEmbryoDevelopmentTable);
